// src/services/asksService.js
import api, { isAuthenticated, updateAuthToken } from "../utils/api";
import { API_CONFIG } from "../utils/apiConfig";

// ============================================================
// Ask formatting / normalization
//
// The /app/personal/get-asks endpoint returns structured asks:
//   parsed_json = {
//     schemaVersion, marketSlug, menuItemId,
//     formValues: { device_type, device_model, issue, budget_range, ... }
//   }
//   location = { latitude, longitude }
//
// The raw values (e.g. budget_range '10k_30k', issue
// 'battery, broken_screen') are codes defined by the market's form
// schema, so we decode the known ones into readable labels here and
// expose one consistent display-ready shape to every consumer.
// ============================================================

// Known labels for the computer-village market form schema. Values not
// listed fall back to a humanized version of the code.
const BUDGET_LABELS = {
    // fix_repair
    'under_10k': 'Under ₦10,000',
    '10k_30k': '₦10,000 – ₦30,000',
    '30k_50k': '₦30,000 – ₦50,000',
    'above_100k': 'Above ₦100,000',
    'not_sure': "I'm not sure",
    'custom_budget': 'Enter amount',
    // buy_phone
    'under_50k': 'Under ₦50,000',
    '50k_100k': '₦50,000 – ₦100,000',
    '100k_200k': '₦100,000 – ₦200,000',
    '200k_400k': '₦200,000 – ₦400,000',
    '400k_800k': '₦400,000 – ₦800,000',
    'above_800k': 'Above ₦800,000',
    // buy_laptop
    'under_100k': 'Under ₦100,000',
    '100k_250k': '₦100,000 – ₦250,000',
    '250k_500k': '₦250,000 – ₦500,000',
    '500k_1m': '₦500,000 – ₦1,000,000',
    'above_1m': 'Above ₦1,000,000',
    // buy_accessories
    'under_5k': 'Under ₦5,000',
    '5k_15k': '₦5,000 – ₦15,000',
    '15k_30k': '₦15,000 – ₦30,000',
    '30k_60k': '₦30,000 – ₦60,000',
    'above_60k': 'Above ₦60,000',
};

const MENU_ITEM_LABELS = {
    'fix_repair': 'Fix / Repair',
    'buy_phone': 'Buy Phone / Tablet',
    'buy_laptop': 'Buy Laptop / Computer',
    'swap_trade': 'Swap / Trade-in',
    'buy_accessories': 'Buy Accessories',
};

const DEVICE_TYPE_LABELS = {
    iphone: 'iPhone',
    samsung: 'Samsung Galaxy',
    tecno_infinix: 'Tecno/Infinix',
    other_android: 'Other Android',
    laptop: 'Laptop',
    tablet: 'Tablet/iPad',
    desktop: 'Desktop',
    smartphone: 'Smartphone',
    feature_phone: 'Feature phone',
    macbook: 'MacBook',
    windows_laptop: 'Laptop (Windows)',
    other: 'Other',
};

const ISSUE_LABELS = {
    broken_screen: 'Broken screen',
    battery: 'Battery issue',
    water_damage: 'Water damage',
    charging_port: 'Charging port',
    software: 'Software problem',
    speaker_mic: 'Speaker/mic',
    camera: 'Camera issue',
    other: 'Other',
};

const URGENCY_LABELS = {
    asap: 'ASAP',
    soon: 'Soon',
    flexible: 'Flexible',
    today: 'Today',
    this_week: 'This week',
    browsing: 'Browsing',
    no_rush: 'No rush',
    exploring: 'Exploring',
};

// snake_case / kebab-case codes -> "Title Case" text
const humanize = (value) => {
    if (!value) return '';
    return String(value)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
};

const toNumber = (part) => {
    if (!part) return null;
    const t = String(part).trim().toLowerCase();
    if (!t) return null;
    if (t.endsWith('k')) {
        const n = parseFloat(t);
        return Number.isNaN(n) ? null : n * 1000;
    }
    if (t.endsWith('m')) {
        const n = parseFloat(t);
        return Number.isNaN(n) ? null : n * 1000000;
    }
    const n = parseFloat(t);
    return Number.isNaN(n) ? null : n;
};

// Decode a budget_range code like '10k_30k' into { min, max, text }
const parseBudgetRange = (value) => {
    if (!value) return { min: null, max: null, text: '' };
    const parts = String(value).split('_').filter(Boolean);
    let min = null;
    let max = null;
    if (parts.length >= 2) {
        min = toNumber(parts[0]);
        max = toNumber(parts[1]);
    } else if (parts.length === 1) {
        max = toNumber(parts[0]);
    }
    return { min, max, text: BUDGET_LABELS[value] || humanize(value) };
};

// 'battery, broken_screen, software' -> 'Battery issue, Broken screen, Software problem'
const decodeIssueLabels = (issue) =>
    String(issue || '')
        .split(',')
        .map((code) => ISSUE_LABELS[code.trim()] || humanize(code.trim()))
        .filter(Boolean);

// Decode HTML entities in text
const decodeEntities = (value) =>
    String(value || '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');

// Literal unicode-escape text (e.g. `u20A610,000 u2013 u20A630,000` or
// `\u20A610,000 \u2013 \u20A630,000`) -> the real characters (₦10,000 – ₦30,000)
const decodeUnicodeEscapes = (value) => {
    const s = String(value || '');
    if (!/\\?u[0-9a-fA-F]{4}/.test(s)) return s;
    const fromHex = (h) => String.fromCodePoint(parseInt(h, 16));
    return s
        .replace(/\\u([0-9a-fA-F]{4})/g, (m, h) => fromHex(h))
        .replace(/(^|[^\w])u([0-9a-fA-F]{4})/g, (m, pre, h) => pre + fromHex(h));
};

// Strip HTML tags from the backend-generated message_body into plain text
const stripHtml = (value) =>
    String(value || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+\n/g, '\n')
        .trim();

// message_body -> readable plain text for cards/chats
const messageBodyToText = (value) => decodeEntities(stripHtml(decodeUnicodeEscapes(value)));

const buildTitle = ({ formValues, menuItemId }) => {
    const f = formValues || {};
    const menuLabel = MENU_ITEM_LABELS[menuItemId] || humanize(menuItemId);
    const model = f.device_model || f.my_device_model || f.target_device;
    const deviceType = DEVICE_TYPE_LABELS[f.device_type] || humanize(f.device_type);
    const subject = model || deviceType;
    return [subject, menuLabel].filter(Boolean).join(' — ');
};

const buildDescription = ({ formValues, searchQuery }) => {
    const f = formValues || {};
    const parts = [];
    const model = f.device_model || f.my_device_model;
    if (model) parts.push(`Device: ${model}`);
    const issue = decodeIssueLabels(f.issue);
    if (issue.length) parts.push(`Issue: ${issue.join(', ')}`);
    if (f.swap_type) parts.push(`Request: ${humanize(f.swap_type)}`);
    if (f.type) parts.push(`Type: ${humanize(f.type)}`);
    if (f.accessory_type) parts.push(`Item: ${humanize(f.accessory_type)}`);
    if (f.issue_details) parts.push(f.issue_details);
    return parts.join('\n') || searchQuery || '';
};

const buildAttributes = ({ formValues }) => {
    const f = formValues || {};
    const attrs = [];
    const deviceType = DEVICE_TYPE_LABELS[f.device_type] || humanize(f.device_type);
    if (deviceType) attrs.push(deviceType);
    attrs.push(...decodeIssueLabels(f.issue));
    const urgency = URGENCY_LABELS[f.urgency] || humanize(f.urgency);
    if (urgency) attrs.push(urgency);
    return attrs.slice(0, 6);
};

const buildLocation = (ask) => {
    const loc = ask.location || {};
    const lat = loc.latitude ?? loc.lat ?? ask.latitude;
    const lon = loc.longitude ?? loc.lon ?? ask.longitude;
    if (lat != null && lon != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon))) {
        return `${Number(lat).toFixed(4)}, ${Number(lon).toFixed(4)}`;
    }
    if (ask.search_query) {
        const match = ask.search_query.match(/(?:in|at|near)\s+([A-Za-z\s]+)/i);
        if (match) return match[1].trim();
    }
    return 'Location not specified';
};

/**
 * Normalize a raw ask from the backend into a consistent display-ready
 * shape (decodes form schema codes, builds a readable title/description,
 * formats budget and location).
 * @param {Object} ask - Raw ask object from the API
 * @returns {Object} Formatted ask
 */
export const formatAsk = (ask) => {
    if (!ask || typeof ask !== 'object') return ask;

    let parsed = ask.parsed_json || {};
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch (e) {
            parsed = {};
        }
    }
    const formValues = parsed.formValues || {};

    const isExpired =
        ask.formatted_expires === 'Expired' ||
        (ask.expires_at && new Date(ask.expires_at) < new Date());

    const rawTitle = buildTitle({ formValues, menuItemId: parsed.menuItemId });
    const description = buildDescription({ formValues, searchQuery: ask.search_query }) ||
        'Buyer posted a new request.';
    const budget = parseBudgetRange(formValues.budget_range);
    const menuLabel = MENU_ITEM_LABELS[parsed.menuItemId] || humanize(parsed.menuItemId);

    return {
        id: ask.id,
        askId: ask.id,
        title: rawTitle || (ask.search_query ? ask.search_query.slice(0, 60) : `Ask #${String(ask.id).slice(0, 8)}`),
        description,
        messageBody: messageBodyToText(ask.message_body) || description,
        snippet: description.substring(0, 100),
        status: isExpired ? 'expired' : (ask.has_responded ? 'responded' : 'active'),
        price: budget.max,
        minPrice: budget.min,
        budgetText: budget.text,
        budgetRange: formValues.budget_range || null,
        location: buildLocation(ask),
        latitude: ask.location?.latitude ?? ask.latitude ?? null,
        longitude: ask.location?.longitude ?? ask.longitude ?? null,
        category: menuLabel || humanize(parsed.marketSlug) || 'other',
        attributes: buildAttributes({ formValues }),
        brand: formValues.device_model || formValues.my_device_model || null,
        timeline: URGENCY_LABELS[formValues.urgency] || humanize(formValues.urgency) || null,
        menuItemId: parsed.menuItemId || null,
        marketSlug: parsed.marketSlug || null,
        formValues,
        parsed_json: parsed,
        createdAt: ask.created_at,
        expiresAt: ask.expires_at,
        formattedExpires: ask.formatted_expires,
        expiresSoon: ask.expires_soon,
        buyerName: ask.buyer_name || 'Unknown Buyer',
        buyerLogo: ask.buyer_logo || null,
        qualityScore: ask.quality_score,
        responseCount: ask.response_count,
        isExpired,
        hasResponded: !!ask.has_responded,
        imageUrls: Array.isArray(ask.image_urls) ? ask.image_urls : [],
        videoUrls: Array.isArray(ask.video_urls) ? ask.video_urls : [],
        searchQuery: ask.search_query,
    };
};

/**
 * Fetch asks data from the endpoint
 * @param {Object} params - Query parameters (page, limit, etc.)
 * @returns {Promise} - Promise with the asks data
 */
export const fetchAsksData = async (params = {}) => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
        console.warn('No bearer token found. User may not be authenticated.');
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.get(API_CONFIG.ASKS.GET_ASKS, { params });
        
        // Handle different response structures
        let rawAsks = [];
        let pagination = null;
        const body = response.data;
        if (body && body.success) {
            if (Array.isArray(body.asks)) {
                rawAsks = body.asks;
            } else if (Array.isArray(body.data?.asks)) {
                rawAsks = body.data.asks;
            } else if (Array.isArray(body.data)) {
                rawAsks = body.data;
            }
            pagination = body.pagination || body.data?.pagination || null;
        } else if (Array.isArray(body)) {
            rawAsks = body;
        } else if (body?.data && Array.isArray(body.data)) {
            rawAsks = body.data;
            pagination = body.data.pagination || null;
        } else if (Array.isArray(body?.asks)) {
            rawAsks = body.asks;
            pagination = body.pagination || null;
        }
        
        const asks = rawAsks.map(formatAsk);
        
        return {
            success: true,
            data: asks,
            asks,
            pagination,
            status: response.status,
            headers: response.headers,
            rawData: response.data
        };
    } catch (error) {
        console.error('Error fetching asks data:', error);
        
        // Handle specific error cases
        if (error.response?.status === 401) {
            return {
                success: false,
                error: 'Session expired. Please log in again.',
                status: 401,
                data: null
            };
        }
        
        if (error.response?.status === 403) {
            return {
                success: false,
                error: 'You do not have permission to access this resource.',
                status: 403,
                data: null
            };
        }
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch asks data',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Fetch a single ask by ID
 * @param {string|number} askId - The ID of the ask to fetch
 * @returns {Promise} - Promise with the ask data
 */
export const fetchAskById = async (askId) => {
    if (!isAuthenticated()) {
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.get(`${API_CONFIG.ASKS.GET_ASK}/${askId}`);
        
        return {
            success: true,
            data: response.data?.data || response.data,
            status: response.status
        };
    } catch (error) {
        console.error('Error fetching ask by ID:', error);
        
        if (error.response?.status === 404) {
            return {
                success: false,
                error: 'Ask not found',
                status: 404,
                data: null
            };
        }
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch ask',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Submit an offer/response to an ask
 * @param {string|number} askId - The ID of the ask to respond to
 * @param {Object} offerData - The offer data
 * @param {number} offerData.price - The offer price
 * @param {string} offerData.offer - The offer description/message
 * @param {number} offerData.quantity - The quantity (optional)
 * @param {string} offerData.delivery_time - Delivery option (optional)
 * @param {string} offerData.notes - Additional notes/location (optional)
 * @returns {Promise} - Promise with the response
 */
export const submitOfferToAsk = async (askId, offerData) => {
    if (!isAuthenticated()) {
        console.warn('No bearer token found. User may not be authenticated.');
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    if (!askId) {
        return {
            success: false,
            error: 'Ask ID is required',
            status: 400,
            data: null
        };
    }
    
    if (!offerData.price || offerData.price <= 0) {
        return {
            success: false,
            error: 'Valid price is required',
            status: 400,
            data: null
        };
    }
    
    try {
        // Format the request body according to the external endpoint expectations
        const requestBody = {
            price: parseFloat(offerData.price),
            offer: offerData.offer || offerData.personalNote || 'Offer submitted',
            quantity: offerData.quantity || 1,
            delivery_time: offerData.delivery_time || offerData.deliveryOption || 'pickup',
            notes: offerData.notes || offerData.location || '',
            latitude: offerData.latitude != null ? parseFloat(offerData.latitude) : null,
            longitude: offerData.longitude != null ? parseFloat(offerData.longitude) : null,
            image_urls: offerData.image_urls || offerData.images || null,
            video_urls: offerData.video_urls || null
        };
        
        console.log('Submitting offer to ask:', { askId, requestBody });
        
        const response = await api.post(`${API_CONFIG.ASKS.ASK_RESPONSE}/${askId}/response`, requestBody);
        
        // Handle different response structures
        let responseData = response.data?.data || response.data;
        
        return {
            success: true,
            data: responseData,
            message: response.data?.message || 'Offer submitted successfully',
            status: response.status,
            offerDetails: {
                askId: askId,
                price: requestBody.price,
                offer: requestBody.offer,
                quantity: requestBody.quantity,
                delivery_time: requestBody.delivery_time,
                submittedAt: new Date().toISOString()
            }
        };
    } catch (error) {
        console.error('Error submitting offer:', error);
        
        if (error.response?.status === 401) {
            return {
                success: false,
                error: 'Session expired. Please log in again.',
                status: 401,
                data: null
            };
        }
        
        if (error.response?.status === 403) {
            return {
                success: false,
                error: 'You do not have permission to submit an offer for this ask.',
                status: 403,
                data: null
            };
        }
        
        if (error.response?.status === 404) {
            return {
                success: false,
                error: 'The ask you are trying to respond to does not exist.',
                status: 404,
                data: null
            };
        }
        
        if (error.response?.status === 409) {
            return {
                success: false,
                error: 'You have already submitted an offer for this ask.',
                status: 409,
                data: null
            };
        }
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to submit offer',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Fetch offers for a specific ask
 * @param {string|number} askId - The ID of the ask
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise} - Promise with the offers data
 */
export const fetchOffersForAsk = async (askId, params = {}) => {
    if (!isAuthenticated()) {
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.get(`${API_CONFIG.ASKS.ASK_OFFERS}/${askId}/offers`, { params });
        
        let offersData = [];
        if (response.data && response.data.success) {
            offersData = response.data.data || response.data.offers || [];
        } else if (response.data && Array.isArray(response.data)) {
            offersData = response.data;
        } else {
            offersData = response.data?.offers || [];
        }
        
        return {
            success: true,
            data: offersData,
            status: response.status,
            pagination: response.data?.pagination || null
        };
    } catch (error) {
        console.error('Error fetching offers:', error);
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch offers',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Update an existing offer
 * @param {string|number} askId - The ID of the ask
 * @param {string|number} offerId - The ID of the offer
 * @param {Object} updateData - The data to update
 * @returns {Promise} - Promise with the response
 */
export const updateOffer = async (askId, offerId, updateData) => {
    if (!isAuthenticated()) {
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.put(`${API_CONFIG.ASKS.ASK_OFFERS}/${askId}/offers/${offerId}`, updateData);
        
        return {
            success: true,
            data: response.data?.data || response.data,
            message: response.data?.message || 'Offer updated successfully',
            status: response.status
        };
    } catch (error) {
        console.error('Error updating offer:', error);
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update offer',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Delete/withdraw an offer
 * @param {string|number} askId - The ID of the ask
 * @param {string|number} offerId - The ID of the offer
 * @returns {Promise} - Promise with the response
 */
export const deleteOffer = async (askId, offerId) => {
    if (!isAuthenticated()) {
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.delete(`${API_CONFIG.ASKS.ASK_OFFERS}/${askId}/offers/${offerId}`);
        
        return {
            success: true,
            message: response.data?.message || 'Offer withdrawn successfully',
            status: response.status
        };
    } catch (error) {
        console.error('Error deleting offer:', error);
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to withdraw offer',
            status: error.response?.status || 500
        };
    }
};

/**
 * Post ask data to the endpoint
 * @param {Object} askData - The ask data to submit
 * @returns {Promise} - Promise with the response
 */
export const postAskData = async (askData) => {
    if (!isAuthenticated()) {
        console.warn('No bearer token found. User may not be authenticated.');
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.post(API_CONFIG.ASKS.POST_ASK, askData);
        
        return {
            success: true,
            data: response.data?.data || response.data,
            message: response.data?.message || 'Ask created successfully',
            status: response.status
        };
    } catch (error) {
        console.error('Error posting ask data:', error);
        
        if (error.response?.status === 401) {
            return {
                success: false,
                error: 'Session expired. Please log in again.',
                status: 401,
                data: null
            };
        }
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to post ask data',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Update ask data
 * @param {string|number} askId - The ID of the ask to update
 * @param {Object} updateData - The data to update
 * @returns {Promise} - Promise with the response
 */
export const updateAskData = async (askId, updateData) => {
    if (!isAuthenticated()) {
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.put(`${API_CONFIG.ASKS.UPDATE_ASK}/${askId}`, updateData);
        
        return {
            success: true,
            data: response.data?.data || response.data,
            message: response.data?.message || 'Ask updated successfully',
            status: response.status
        };
    } catch (error) {
        console.error('Error updating ask data:', error);
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update ask data',
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Delete ask data
 * @param {string|number} askId - The ID of the ask to delete
 * @returns {Promise} - Promise with the response
 */
export const deleteAskData = async (askId) => {
    if (!isAuthenticated()) {
        return {
            success: false,
            error: 'Authentication required. Please log in.',
            status: 401,
            data: null
        };
    }
    
    try {
        const response = await api.delete(`${API_CONFIG.ASKS.DELETE_ASK}/${askId}`);
        
        return {
            success: true,
            message: response.data?.message || 'Ask deleted successfully',
            status: response.status
        };
    } catch (error) {
        console.error('Error deleting ask data:', error);
        
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete ask data',
            status: error.response?.status || 500
        };
    }
};

